import "./DPStaticDialog.css";

// Add a shared variable to track the current active dialog
let activeDialog: Office.Dialog | null = null;

// Common dialog configuration options
const DEFAULT_DIALOG_OPTIONS = {
  height: 25,
  width: 35,
  displayInIframe: true
};

// Helper function to wait for dialog closure
async function waitForDialogClose(delay: number = 300): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, delay));
}

// Helper function to safely close a dialog
export async function safeCloseDialog(dialog: Office.Dialog | null): Promise<void> {
  if (dialog) {
    try {
      dialog.close();
      await waitForDialogClose();
    } catch (error) {
      // Error code 12006 means dialog is already closed
      if (error.code !== 12006) {
        logger.error("[DPStaticDialog] Error closing dialog:", error);
      }
    }
  }
}

/**
 * Creates and displays a dialog using the Office Dialog API
 * @param url The URL to load in the dialog
 * @param options Dialog display options
 * @returns Promise resolving to the dialog object or null if it failed
 */
async function createDialog(url: string, options = DEFAULT_DIALOG_OPTIONS): Promise<Office.Dialog | null> {
  return new Promise((resolve) => {
    try {
      logger.debug("[DPStaticDialog] Creating dialog for URL:", url);
      
      // Make sure we're only passing the display options without any callback
      const displayOptions = {
        height: options.height,
        width: options.width,
        displayInIframe: options.displayInIframe
      };
      
      Office.context.ui.displayDialogAsync(
        url,
        displayOptions,
        function(result) {
          if (result.status === Office.AsyncResultStatus.Failed) {
            logger.error("[DPStaticDialog] Failed to display dialog:", 
              result.error ? `${result.error.code}: ${result.error.message}` : "Unknown error");
            resolve(null);
          } else {
            const dialog = result.value;
            activeDialog = dialog;
            logger.debug("[DPStaticDialog] Dialog opened successfully");
            resolve(dialog);
          }
        }
      );
    } catch (error) {
      logger.error("[DPStaticDialog] Error creating dialog:", error);
      resolve(null);
    }
  });
}

// Function to show a progress dialog
export async function showProgressDialog(url: string): Promise<Office.Dialog> {
  // First safely close any existing dialog
  if (activeDialog) {
    await safeCloseDialog(activeDialog);
    activeDialog = null;
  }

  return new Promise((resolve, reject) => {
    createDialog(url)
      .then(dialog => {
        if (!dialog) {
          reject(new Error("Failed to create progress dialog"));
          return;
        }

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
          logger.debug("[DPStaticDialog] Progress dialog event received:", 
            'error' in arg ? arg.error : arg.message);
          if (activeDialog === dialog) {
            activeDialog = null;
          }
        });
        
        resolve(dialog);
      })
      .catch(error => {
        logger.error("[DPStaticDialog] Error in showProgressDialog:", error);
        reject(error);
      });
  });
}

// Direct async function that doesn't use browser APIs
export async function showWarningAndConfirm(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    logger.debug("[DPStaticDialog] Showing warning confirmation");
    
    try {
      if (!Office.context.ui || !Office.context.ui.displayDialogAsync) {
        logger.debug("[DPStaticDialog] No UI methods available, defaulting to continue");
        resolve(true);
        return;
      }

      let dialogClosed = false;
      let timeoutId: number;
      
      // Set up timeout that will fire if dialog doesn't respond in 2 minutes
      timeoutId = window.setTimeout(() => {
        if (!dialogClosed) {
          logger.debug("[DPStaticDialog] Dialog timed out after 2 minutes, canceling operation");
          dialogClosed = true;
          
          // Close the dialog if we have a reference to it
          if (activeDialog) {
            safeCloseDialog(activeDialog)
              .then(() => {
                logger.debug("[DPStaticDialog] Dialog closed on timeout");
                activeDialog = null;
              })
              .catch(error => {
                logger.error("[DPStaticDialog] Error closing dialog on timeout:", error);
              });
          }
          
          resolve(false); // Resolve with false (cancel) on timeout
        }
      }, 120000); // 2 minutes in milliseconds
      
      createDialog(url)
        .then(dialog => {
          if (!dialog) {
            // Dialog failed to open, resolve with false
            if (!dialogClosed) {
              dialogClosed = true;
              clearTimeout(timeoutId);
              resolve(false);
            }
            return;
          }
          
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
            logger.debug("[DPStaticDialog] Message received:", 'message' in arg ? arg.message : 'No message');
            
            // Clear timeout since we got a response
            clearTimeout(timeoutId);
            
            safeCloseDialog(dialog)
              .then(() => logger.debug("[DPStaticDialog] Dialog closed after message received"))
              .catch(error => logger.error("[DPStaticDialog] Error closing dialog after message:", error));
            
            // Only resolve if we haven't already resolved from timeout
            if (!dialogClosed) {
              dialogClosed = true;
              activeDialog = null;
              resolve('message' in arg && arg.message === 'continue');
            }
          });
          
          dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
            logger.debug("[DPStaticDialog] Event received:", 'error' in arg ? arg.error : 'No error');
            
            // Clear timeout since we got a response
            clearTimeout(timeoutId);
            
            // Event 12006 means the dialog was closed
            if (!('error' in arg && arg.error === 12006)) {
              safeCloseDialog(dialog)
                .then(() => logger.debug("[DPStaticDialog] Dialog closed after event"))
                .catch(error => logger.error("[DPStaticDialog] Error closing dialog after event:", error));
            } else {
              logger.debug("[DPStaticDialog] Dialog was closed by user");
            }
            
            // Only resolve if we haven't already resolved from timeout
            if (!dialogClosed) {
              dialogClosed = true;
              activeDialog = null;
              resolve(false); // Default to cancel
            }
          });
        })
        .catch(error => {
          logger.error("[DPStaticDialog] Error creating dialog:", error);
          
          // Clear timeout and resolve if we haven't already
          if (!dialogClosed) {
            dialogClosed = true;
            clearTimeout(timeoutId);
            resolve(false);
          }
        });
    } catch (error) {
      logger.error("[DPStaticDialog] Error in showWarningAndConfirm:", error);
      // Default to continuing the operation if there's any error
      resolve(true);
    }
  });
} 